<template>
  <div>
    <div :style="{ opacity: headerSettings ? '1' : '0' }">
      <HeaderDefault v-if="headerSettings?.isHeaderNavActive" :blok="headerSettings" />
    </div>
    <slot></slot>
    <FooterDefault v-if="footerSettings" :blok="footerSettings" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { CommonBlok, FooterDefaultBlok, HeaderDefaultBlok } from '~/types/storyblok'

const headerSettings = ref<Omit<HeaderDefaultBlok, keyof CommonBlok> | null>(null)
const footerSettings = ref<Omit<FooterDefaultBlok, keyof CommonBlok> | null>(null)

const story = useStoryData()
const { storyData } = storeToRefs(story)
const { pageTrackingData } = useGaTrackingStore()
const { trackDatalayer } = useTracking()
const { headerPath, footerPath } = await useSettingsPath()

const [headerDefaultSettings, footerDefaultSettings] = await Promise.all([
  headerPath ? useStoryblokPage<HeaderDefaultBlok>(headerPath) : Promise.resolve(null),
  footerPath ? useStoryblokPage<FooterDefaultBlok>(footerPath) : Promise.resolve(null),
])

headerSettings.value = {
  ...headerDefaultSettings?.content,
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- using the settings from headerDefaultSettings on false is intentional
  isHeaderNavActive: storyData.value?.content.isHeaderNavActive || headerDefaultSettings?.content.isHeaderNavActive,
  navLinks: (headerDefaultSettings?.content.navLinks ?? []).concat(storyData.value?.content.navLinks ?? []),
  headerLogo: storyData.value?.content.Logo && (storyData.value.content.Logo.length ?? 0 > 0) ? [storyData.value.content.Logo[0]] : headerDefaultSettings?.content.headerLogo,
  specialNavLinks: (headerDefaultSettings?.content.specialNavLinks ?? []).concat(storyData.value?.content.specialNavLinks ?? []),
  cta: storyData.value?.content.cta,
}

footerSettings.value = {
  ...footerDefaultSettings?.content,
  disclaimerLinks: footerDefaultSettings?.content.disclaimerLinks,
  footerAddLinks: footerDefaultSettings?.content.footerAddLinks,
  isTrustpilotFooterHidden: storyData.value?.content.isTrustpilotFooterHidden ?? footerDefaultSettings?.content.isTrustpilotFooterHidden,
  isTuevFooterHidden: storyData.value?.content.isTuevFooterHidden ?? footerDefaultSettings?.content.isTuevFooterHidden,
}

// track only on client-side
onMounted(() => {
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- we don't just want "unset" on nullish but also on empty string */
  pageTrackingData.content_group = storyData.value?.content.ga_content_group || 'unset'
  pageTrackingData.content_type = storyData.value?.content.ga_content_type || 'unset'
  pageTrackingData.content_name = storyData.value?.content.ga_content_name || 'unset'
  pageTrackingData.content_id = storyData.value?.content.ga_content_id || 'unset'
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing -- we don't just want "unset" on nullish but also on empty string */

  trackDatalayer(toRaw(pageTrackingData))
})
</script>
